import { createHash } from "node:crypto"
import fs from "node:fs"
import { globSync } from "glob"
import path from "node:path"

const ignoredDirectories = [
  "**/.git/**",
  "**/.next/**",
  "**/.supernova/**",
  "**/build/**",
  "**/coverage/**",
  "**/dist/**",
  "**/node_modules/**",
  "**/out/**",
]

export type CollectFilesInput = {
  /**
   * Exclude files that belong to nested packages under projectRoot. A nested package is any subdirectory
   * with its own package.json, so those files should be analyzed only when that package is scanned directly.
   */
  excludeNestedPackages?: boolean
  excludePaths?: string[]
  pattern: string
  projectRoot: string
}

export function normalizeExcludePath(excludePath: string): string {
  return excludePath.trim().replaceAll("\\", "/").replace(/^\.?\//, "").replace(/\/+$/, "")
}

function isExcluded(relativePath: string, excludePaths: string[]): boolean {
  return excludePaths.some(excludePath => relativePath === excludePath || relativePath.startsWith(`${excludePath}/`))
}

function nestedPackageDirs(projectRoot: string): string[] {
  if (!fs.existsSync(path.join(projectRoot, "package.json"))) {
    return []
  }

  return globSync("**/package.json", {
    cwd: projectRoot,
    dot: false,
    ignore: ignoredDirectories,
    nodir: true,
  })
    .map(packageJsonPath => normalizeExcludePath(path.dirname(packageJsonPath)))
    .filter(packageDir => packageDir !== ".")
}

export async function collectFiles(input: CollectFilesInput): Promise<string[]> {
  const explicitExcludePaths = (input.excludePaths ?? [])
    .map(excludePath => normalizeExcludePath(excludePath))
    .filter(path => path.length > 0)
  const packageExcludePaths = input.excludeNestedPackages ? nestedPackageDirs(input.projectRoot) : []
  const excludePaths = [...new Set([...explicitExcludePaths, ...packageExcludePaths])]
  const files = globSync(input.pattern, {
    absolute: true,
    cwd: input.projectRoot,
    dot: false,
    ignore: ignoredDirectories,
    nodir: true,
  })

  const filtered = excludePaths.length > 0
    ? files.filter(file => !isExcluded(toRelative(input.projectRoot, file), excludePaths))
    : files

  return filtered.sort((a, b) => a.localeCompare(b))
}

export function componentKeyFrom(exportName: string, componentPath: string, existing: Set<string>): string {
  const normalized = exportName
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replaceAll(/[^a-zA-Z0-9]+/g, "-")
    .replaceAll(/-{2,}/g, "-")
    .replaceAll(/^-|-$/g, "")
    .toLowerCase()

  if (!existing.has(normalized)) {
    existing.add(normalized)
    return normalized
  }

  const suffix = createHash("sha1").update(componentPath).digest("hex").slice(0, 6)
  const key = `${normalized}-${suffix}`
  existing.add(key)
  return key
}

export function toRelative(projectRoot: string, absolutePath: string): string {
  return path.relative(projectRoot, absolutePath).split(path.sep).join("/")
}

export function uniqueSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b))
}
